import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { AlarmClockIcon } from '../../components/svgs/icons/AlarmClockIcon';
import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Input } from '../Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { styleSheet } from './styles';

type Props = {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	/** When set (24h "HH:mm"), the preset list only offers times strictly after this one. */
	minTime?: string;
};

// Strict h and H tokens reject a leading zero, so padded hours like 09 need their own formats.
const PARSE_FORMATS = [
	'HH:mm',
	'H:mm',
	'hh:mm a',
	'h:mm a',
	'hh:mma',
	'h:mma',
	'hh a',
	'h a',
	'hha',
	'ha',
	'HH',
	'h',
	'H',
];

export const normalizeTimeInput = (raw: string): string | null => {
	const trimmed = raw.trim();
	if (!trimmed) {
		return null;
	}
	const parsed = moment(trimmed.toLowerCase().replace(/\s+/g, ' '), PARSE_FORMATS, true);
	return parsed.isValid() ? parsed.format('HH:mm') : null;
};

const OPTIONS: string[] = Array.from({ length: 96 }, (_, i) =>
	moment()
		.startOf('day')
		.add(i * 15, 'minutes')
		.format('HH:mm')
);

const toLabel = (hhmm: string) => moment(hhmm, 'HH:mm').format('h:mm A');

// The field always displays 12h (matching the preset list); onChange still only ever
// receives the 24h HH:mm (or '') that the rest of the Volunteers module composes with.
const externalDisplay = (hhmm: string) => (hhmm ? toLabel(hhmm) : '');

export const TimeInput = ({ value, onChange, disabled, minTime }: Props) => {
	const [localValue, setLocalValue] = useState(() => externalDisplay(value));
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setLocalValue(externalDisplay(value));
	}, [value]);

	const commit = () => {
		if (localValue === externalDisplay(value)) {
			return;
		}
		if (!localValue.trim()) {
			onChange('');
			return;
		}
		const normalized = normalizeTimeInput(localValue);
		if (normalized) {
			setLocalValue(toLabel(normalized));
			onChange(normalized);
		} else {
			setLocalValue(externalDisplay(value));
		}
	};

	const pick = (hhmm: string) => {
		setLocalValue(toLabel(hhmm));
		onChange(hhmm);
		setIsOpen(false);
	};

	const selectedOption = normalizeTimeInput(localValue);
	const options = minTime ? OPTIONS.filter(option => option > minTime) : OPTIONS;

	return (
		<ButtonGroup>
			<Popover open={isOpen} onOpenChange={setIsOpen}>
				<PopoverTrigger asChild>
					<Button variant='secondary' disabled={disabled}>
						<AlarmClockIcon />
					</Button>
				</PopoverTrigger>
				<PopoverContent side='bottom'>
					<div className={css(styleSheet.options)}>
						{options.map(option => (
							<Button
								key={option}
								variant='ghost'
								styles={[styleSheet.optionButton, option === selectedOption ? styleSheet.optionButtonSelected : null]}
								onClick={() => pick(option)}
								type='button'
							>
								{toLabel(option)}
							</Button>
						))}
					</div>
				</PopoverContent>
			</Popover>
			<Input
				value={localValue}
				onChange={e => setLocalValue(e.target.value)}
				onBlur={commit}
				placeholder='h:mm A'
				disabled={disabled}
			/>
		</ButtonGroup>
	);
};
